import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Icon } from '../components/sp-primitives';
import { Modal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { ROUTES } from '../routes';

function formatDateISO(value) {
  if (value === undefined || value === null || value === '') return null;
  const s = String(value).trim();
  const isoDay = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDay) return `${isoDay[1]}-${isoDay[2]}-${isoDay[3]}`;
  const d = value instanceof Date ? value : new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function formatDateBR(value) {
  const iso = formatDateISO(value);
  if (!iso) return '[ausente]';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function resolveName(id, map) {
  if (id === null || id === undefined || id === '') return '[ausente]';
  return map[id] ?? String(id);
}

function PApprove() {
  const app = useApp();
  const [debugByImport, setDebugByImport] = useState({});
  const [rejectModal, setRejectModal] = useState({ open: false, batchId: null, notes: '', submitting: false });
  const [lookupMaps, setLookupMaps] = useState({ debtors: {}, docIdents: {}, categories: {} });
  const [expandedDebug, setExpandedDebug] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const activeCompanyId = Number(app.activeCompany?.id || 0);
  const permissions = Array.isArray(app.currentUser?.permissions) ? app.currentUser.permissions : [];
  const canReviewPayments = permissions.includes('Aprovador') || permissions.includes('Administrador');

  const showReviewerWaitMessage = () => {
    app.toast('info', 'Aprovações', 'Aguardando Aprovadores ou Administradores analisarem o pagamento');
  };

  const handleApprove = (batchId) => {
    if (!canReviewPayments) {
      showReviewerWaitMessage();
      return;
    }
    app.approveBatch(batchId);
  };

  const handleReject = (batchId) => {
    if (!canReviewPayments) {
      showReviewerWaitMessage();
      return;
    }
    setRejectModal({ open: true, batchId, notes: '', submitting: false });
  };

  const confirmReject = async () => {
    if (!rejectModal.notes.trim()) return;
    setRejectModal((m) => ({ ...m, submitting: true }));
    try {
      await app.rejectBatch(rejectModal.batchId, rejectModal.notes.trim());
      setRejectModal({ open: false, batchId: null, notes: '', submitting: false });
    } catch {
      setRejectModal((m) => ({ ...m, submitting: false }));
    }
  };

  useLayoutEffect(() => {
    if (!app.workspaceReady) return;
    if (app.companies.length === 0 && app.templates.length === 0) {
      app.navigate(ROUTES.EMPTY, { shell: 'approve' });
    }
  }, [app.workspaceReady, app.companies.length, app.templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!app.workspaceReady || !activeCompanyId) return;
    let cancelled = false;
    (async () => {
      try {
        const [fieldsData, companiesData] = await Promise.all([
          app.fetchSiengeFieldsOptions(),
          app.fetchCompanyFieldsCompanies(activeCompanyId),
        ]);
        if (cancelled) return;

        const docIdents = {};
        (fieldsData?.effective?.document_identifications || []).forEach((item) => {
          if (item.value) docIdents[item.value] = item.name;
        });

        const categories = {};
        (fieldsData?.effective?.payment_categories || []).forEach((item) => {
          if (item.value) categories[item.value] = item.name;
        });

        const debtors = {};
        (companiesData?.data || []).forEach((item) => {
          if (item.sienge_debtorId) debtors[item.sienge_debtorId] = item.trade_name || item.name;
        });

        setLookupMaps({ debtors, docIdents, categories });
      } catch {
        // lookup maps ficam vazios, usa IDs como fallback
      }
    })();
    return () => { cancelled = true; };
  }, [app.workspaceReady, activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = useMemo(
    () => app.batches.filter((b) => Number(b.companyId || 0) === activeCompanyId && b.status === 'approval'),
    [app.batches, activeCompanyId],
  );
  const pendingImportIds = useMemo(
    () =>
      pending
        .map((b) => Number(b.lotId || 0))
        .filter((id) => id > 0),
    [pending],
  );
  const pendingImportIdsKey = pendingImportIds.join(',');

  useEffect(() => {
    let cancelled = false;
    if (pendingImportIds.length === 0) {
      setDebugByImport({});
      return undefined;
    }

    setDebugByImport((prev) => {
      const next = { ...prev };
      pendingImportIds.forEach((id) => {
        if (!next[id]) next[id] = { loading: true, error: null, payload: null };
      });
      return next;
    });

    (async () => {
      await Promise.all(pendingImportIds.map(async (lotId) => {
        try {
          const data = await app.fetchLotDetail(lotId);
          const payments = Array.isArray(data?.company_payments) ? data.company_payments : [];
          const firstPayment = payments[0] || null;
          const approvals = Array.isArray(data?.approvals) ? data.approvals : [];
          const firstApproval = approvals[0] || null;

          const p = firstPayment ?? {};
          const payload = {
            debtorId: p.sienge_debtorId ?? '[ausente]',
            creditorId: p.sienge_creditorId ?? '[ausente]',
            documentIdentificationId: p.sienge_documentIdentificationId ?? '[ausente]',
            documentNumber: p.sienge_documentNumber ?? '[ausente]',
            issueDate: formatDateISO(firstApproval?.reviewed_at) ?? '[ausente]',
            installmentsNumber: p.sienge_installmentsNumber ?? '[ausente]',
            indexId: p.sienge_indexId ?? '[ausente]',
            baseDate: formatDateISO(p.sienge_baseDate) ?? '[ausente]',
            dueDate: formatDateISO(p.sienge_dueDate) ?? '[ausente]',
            billDate: formatDateISO(p.sienge_billDate) ?? '[ausente]',
            totalInvoiceAmount: p.sienge_totalInvoiceAmount ?? '[ausente]',
            notes: p.sienge_notes ?? '[ausente]',
            discount: p.sienge_discount ?? '[ausente]',
            budgetCategories: [
              {
                costCenterId: p.sienge_costCenterId ?? '[ausente]',
                paymentCategoriesId: p.sienge_paymentCategoriesId ?? '[ausente]',
                percentage: p.sienge_percentage ?? '[ausente]',
              },
            ],
          };

          const paymentSnapshot = firstPayment ? {
            creditor_name: firstPayment.creditor_name ?? null,
            sienge_dueDate: firstPayment.sienge_dueDate ?? null,
            sienge_documentNumber: firstPayment.sienge_documentNumber ?? null,
            sienge_debtorId: firstPayment.sienge_debtorId ?? null,
            sienge_paymentCategoriesId: firstPayment.sienge_paymentCategoriesId ?? null,
            sienge_documentIdentificationId: firstPayment.sienge_documentIdentificationId ?? null,
            sienge_notes: firstPayment.sienge_notes ?? null,
          } : null;

          if (cancelled) return;
          setDebugByImport((prev) => ({
            ...prev,
            [lotId]: {
              loading: false,
              error: null,
              paymentId: firstPayment?.id ?? null,
              payload,
              paymentSnapshot,
            },
          }));
        } catch (error) {
          if (cancelled) return;
          setDebugByImport((prev) => ({
            ...prev,
            [lotId]: {
              loading: false,
              error: error?.message || 'Falha ao obter detalhes.',
              payload: null,
              paymentSnapshot: null,
            },
          }));
        }
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [app.fetchLotDetail, pendingImportIdsKey]);

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="approve" title="A carregar aprovações…" />;
  }
  if (app.companies.length === 0 && app.templates.length === 0) {
    return null;
  }

  return (
    <PShell active="approve">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Fila de aprovação</div>
          <h1 className="sp-screen-head__title">{pending.length} lote{pending.length === 1 ? '' : 's'} aguardando você</h1>
        </div>
        <div>
          <button type="button" className="sp-btn sp-btn-outline" onClick={() => app.navigate(ROUTES.PAYMENT_LOGS)}>
            Logs de Pagamentos
          </button>
        </div>
      </div>

      {pending.length === 0 ? (
        <div className="sp-card sp-approve-empty">
          <div className="sp-approve-empty__icon"><Icon name="check" size={28} stroke={2} /></div>
          <div className="sp-approve-empty__title">Fila zerada!</div>
          <div className="sp-approve-empty__sub">Todos os lotes foram aprovados ou rejeitados.</div>
        </div>
      ) : (
        <div className="sp-approve-queue">
          {pending.map((b) => (
            <div key={b.id} className="sp-card sp-approve-card">
              <div className={`sp-approve-card__icon${b.diffs > 0 ? ' sp-approve-card__icon--warn' : ' sp-approve-card__icon--ok'}`}><Icon name={b.diffs > 0 ? 'alert' : b.paymentType === 'individual' ? 'user' : 'file'} size={20} /></div>
              <div>
                <div className="sp-approve-card__row">
                  <span className="sp-mono sp-approve-card__id">{b.id}</span>
                  <span className="sp-chip">
                    {debugByImport[b.lotId]?.paymentSnapshot?.sienge_debtorId
                      ? resolveName(debugByImport[b.lotId].paymentSnapshot.sienge_debtorId, lookupMaps.debtors)
                      : b.ref}
                  </span>
                  <span className={`sp-chip ${b.createdByActive ? 'sp-chip-green' : 'sp-chip-red'}`}>{b.createdByName || '-'}</span>
                  {app.currentUser?.id === 1 && (
                    <>
                      <span
                        className="sp-approve-debug-toggle"
                        onClick={() => setExpandedDebug((prev) => ({ ...prev, [b.lotId]: !prev[b.lotId] }))}
                      >
                        [Ver Payload]
                      </span>
                      <span
                        className="sp-approve-debug-toggle"
                        onClick={() => app.navigate('batch-details', { id: b.id })}
                      >
                        [Ver Detalhes]
                      </span>
                      <span
                        className="sp-approve-debug-toggle"
                        onClick={() => setExpandedNotes((prev) => ({ ...prev, [b.lotId]: !prev[b.lotId] }))}
                      >
                        [Ver Anotações]
                      </span>
                    </>
                  )}
                </div>
                {b.paymentType === 'individual' && debugByImport[b.lotId]?.paymentSnapshot ? (
                  <>
                    <div className="sp-approve-card__template">
                      {debugByImport[b.lotId].paymentSnapshot.creditor_name || b.creditorNames[0] || '[ausente]'}
                    </div>
                    <div className="sp-mono sp-approve-card__meta">
                      {debugByImport[b.lotId].paymentSnapshot.sienge_documentNumber || '[ausente]'}
                      {' - '}
                      {resolveName(debugByImport[b.lotId].paymentSnapshot.sienge_paymentCategoriesId, lookupMaps.categories)}
                      {' - '}
                      {resolveName(debugByImport[b.lotId].paymentSnapshot.sienge_documentIdentificationId, lookupMaps.docIdents)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sp-approve-card__template">{b.template}</div>
                    <div className="sp-mono sp-approve-card__meta">{b.file} · {b.colab} colaboradores · solicitado por {b.requesterName || '-'}</div>
                  </>
                )}
              </div>
              <div className="sp-approve-card__values">
                <div className="sp-approve-card__value-block">
                  <div className="sp-caps">QTD. Pag.</div>
                  <div className="sp-num sp-approve-card__qtdpagamentos">{b.colab}</div>
                </div>
                <div className="sp-approve-card__value-block">
                  <div className="sp-caps">Vencimento</div>
                  <div className="sp-num sp-approve-card__vencimento">
                    {b.paymentType === 'individual' && debugByImport[b.lotId]?.paymentSnapshot?.sienge_dueDate
                      ? formatDateBR(debugByImport[b.lotId].paymentSnapshot.sienge_dueDate)
                      : b.dueDate}
                  </div>
                </div>
                <div className="sp-approve-card__value-block">
                  <div className="sp-caps">Valor Total</div>
                  <div className="sp-num sp-approve-card__total">{b.total}</div>
                </div>
              </div>
              <div className="sp-approve-card__actions">
                {b.diffs > 0 && <button type="button" className="sp-btn sp-btn-outline" onClick={() => app.setDiffModalBatch(b)}><Icon name="diff" size={13} /> Revisar</button>}
                <button type="button" className="sp-btn sp-btn-danger-solid" onClick={() => handleReject(b.id)}><Icon name="x" size={13} stroke={2.4} /> Rejeitar</button>
                <button type="button" className="sp-btn" style={{ background: 'var(--ft-green)', color: '#fff' }} onClick={() => handleApprove(b.id)}><Icon name="check" size={13} stroke={2.4} /> Aprovar</button>
              </div>
              {expandedNotes[b.lotId] && (
                <div className="sp-approve-notes-card">
                  {debugByImport[b.lotId]?.paymentSnapshot?.sienge_notes || '[sem anotações]'}
                </div>
              )}
              {expandedDebug[b.lotId] && (
                <div className="sp-approve-debug-card">
                  <pre className="sp-approve-debug-json">
                    {debugByImport[b.lotId]?.payload
                      ? JSON.stringify(debugByImport[b.lotId].payload, null, 2)
                      : '[a carregar...]'}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Modal open={rejectModal.open} onClose={() => !rejectModal.submitting && setRejectModal((m) => ({ ...m, open: false }))} width={500}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Rejeitar Pagamento</div>
            <div className="sp-modal__head-desc">Explique o motivo da rejeição para o registrador.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => !rejectModal.submitting && setRejectModal((m) => ({ ...m, open: false }))} disabled={rejectModal.submitting}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-field-block">
            <label className="sp-caps sp-label">Motivo da rejeição <span className="sp-label-req">obrigatório</span></label>
            <textarea
              className="sp-input"
              rows={4}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Descreva o motivo pelo qual este pagamento está sendo rejeitado..."
              value={rejectModal.notes}
              onChange={(e) => setRejectModal((m) => ({ ...m, notes: e.target.value }))}
              disabled={rejectModal.submitting}
            />
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setRejectModal((m) => ({ ...m, open: false }))} disabled={rejectModal.submitting}>
              Cancelar
            </button>
            <button type="button" className="sp-btn sp-btn-danger-solid" onClick={confirmReject} disabled={rejectModal.submitting || !rejectModal.notes.trim()}>
              {rejectModal.submitting ? 'Rejeitando...' : 'Confirmar Rejeição'}
            </button>
          </div>
        </div>
      </Modal>
    </PShell>
  );
}

export { PApprove };
